import { Box, IconButton, Popper } from '@mui/material'
import { useRef, useState, useEffect } from 'react'
import FilterListAltIcon from '@mui/icons-material/FilterListAlt'
import {
  ColumnHeaderFilterIconButtonProps,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import { FILTER_KINDS, resolveColumnFilter, type ColumnFilterSpec } from './Filter/filterKinds'
import { useAppTheme } from '@/theme/ThemeContext'
import { useTableStore } from '@/store/table/table'

type ColumnFilterSource = {
  headerName?: string
  type?: string
  filterable?: boolean
  filterSpec?: ColumnFilterSpec
  filter?: { type?: string }
}

const CustomColumnFilterMenu = ({
  params,
}: {
  params: ColumnHeaderFilterIconButtonProps
}) => {
  const apiRef = useGridApiContext()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const popperRef = useRef<HTMLDivElement | null>(null)
  const { apiFilters, rowsStore } = useTableStore()

  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  // Check if this column has an active filter
  const hasActiveFilter = apiFilters.some(f => f.name === params.field && f.value.length > 0)

  const colors = {
    icon: isDarkMode ? '#FFFFFF' : '#003F61',
    bg: isDarkMode ? '#1D325A' : '#F8FAFD',
  }

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement

      const insideAnyMuiPopper =
        !!target.closest('.MuiPopper-root') ||
        !!target.closest('.MuiPopover-root')

      const insideThisPopper =
        popperRef.current?.contains(target) ||
        anchorRef.current?.contains(target)

      if (!insideAnyMuiPopper && !insideThisPopper) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('pointerdown', handlePointerDown)
    }

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [open])

  if (params.field === '__check__') return null

  const gridCol = apiRef.current.getColumn(params.field) as ColumnFilterSource | undefined
  const spec = resolveColumnFilter(
    { ...gridCol, field: params.field },
    rowsStore
  )
  if (spec.kind === 'none') return null
  const { Widget } = FILTER_KINDS[spec.kind]

  const toggleFilter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setOpen(open ? false : true)
  }

  return (
    <Box
      onWheel={e => e.stopPropagation()}
      ref={anchorRef}
      sx={{
        width: 18,
        height: 22,
        flex: '0 0 18px',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <IconButton
        sx={{
          color: hasActiveFilter ? (isDarkMode ? '#60A5FA' : '#0B47A8') : colors.icon,
          width: 18,
          height: 22,
          padding: 0,
          '& .MuiSvgIcon-root': { fontSize: 16 },
        }}
        size="small"
        onClick={toggleFilter}
      >
        {open || hasActiveFilter ? (
          <FilterListAltIcon fontSize="small" />
        ) : (
          <FilterAltOutlinedIcon fontSize="small" />
        )}
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 2] } }]}
        style={{
          zIndex: 1300,
          backgroundColor: 'transparent',
          maxWidth: 'min(280px, calc(100vw - 20px))',
        }}
        ref={popperRef}
      >
        <Widget field={params.field} headerName={gridCol?.headerName} spec={spec} />
      </Popper>
    </Box>
  )
}

export default CustomColumnFilterMenu
